import { FC, useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminBorderSystemNormative } from "../../../../../http/border/types/systemNormatives";
import { useSystemNormativesApi } from "../../../../../http/border/useSystemNormatives";
import { PaginationQuery } from "../../../../../types";
import { formatBoolean } from "../../../../../utils";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import CreateSystemNormativeModalDialogButton from "./CreateSystemNormativeModalDialogButton";
import SystemNormativeForm from "./SystemNormativeForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const SystemNormatives: FC<Props> = ({ active }) => {
  const { borderSystemId } = useParams<{ borderSystemId: string }>();
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "systemNormatives" });
  const aborter = useRef(new AbortController());
  const systemNormativesApi = useSystemNormativesApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "systemNormatives");

  const isActiveTab = active ?? true;

  const [itemToEdit, setItemToEdit] = useState<AdminBorderSystemNormative>();
  const [itemToDelete, setItemToDelete] = useState<AdminBorderSystemNormative>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = systemNormativesApi.useAdminSystemNormatives(borderSystemId!, query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.systemNormatives.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.systemNormatives.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateSystemNormativeModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminBorderSystemNormative>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.normatives.singular}</th>
            <th align="left">{i18n.translation.common.isHighWindVisible}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>{item.normative.name}</td>
            <td>{formatBoolean(i18n.translation, item.isHighWindVisible)}</td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.systemNormatives.edit}>
          <SystemNormativeForm systemNormative={itemToEdit} onClose={() => setItemToEdit(undefined)} />
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.systemNormatives.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                systemNormativesApi
                  .deleteAdminSystemNormative(borderSystemId!, itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.systemNormatives.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default SystemNormatives;
